import { Box, Flex } from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import type React from 'react';
import {
	type Dispatch,
	type SetStateAction,
	useCallback,
	useEffect,
	useState,
} from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import FaceliftButton from '@/components/Atoms/FaceliftButton';
import type { SelectItem } from '@/components/Atoms/FaceliftSelect/FaceliftSelect.types';
import type { ExitConfirmationDraftAction } from '@/components/Molecules/FaceliftDialogWithExitConfirmation';
import FaceliftSuccessProgressModal from '@/components/Molecules/FaceliftSuccessProgressModal';
import Show from '@/components/Molecules/Show';
import { Stepper, type StepperStep } from '@/components/Organisms/Stepper';
import { DRAFT_TYPE_LABELS, FORM_TYPES } from '@/constants';
import {
	BgProcessModule,
	useCreateBgProcess,
} from '@/hooks/service-hooks/bgProcess';
import { useGetAccountDetails } from '@/hooks/service-hooks/accounts';
import { useCreateReturnOrder } from '@/hooks/service-hooks/returns';
import {
	useCreateDraft,
} from '@/hooks/service-hooks/drafts';
import { useAuthStore } from '@/store';
import type { Draft } from '@/types/draft';
import { trackCoreClick } from '@/utils/analytics';
import {
	ComplianceChecklist,
	CustomerInformation,
	Preview,
	TissueDetails,
} from '../Steps';
import type { ReturnOrderFormData } from './types';

interface ReturnOrderFormProps {
	onSuccess: () => void;
	onTitleChange?: (title: string) => void;
	onIsDirtyChange?: Dispatch<SetStateAction<boolean>>;
	draftId?: string | null; // Support loading from draft
	existingDraft?: Draft;
	onExitConfirmationDraftActionChange?: (
		action: ExitConfirmationDraftAction | null,
	) => void;
}

const returnOrderSchema: yup.ObjectSchema<ReturnOrderFormData> = yup.object({
	clinic: yup.mixed<SelectItem>().required('Clinic is required'),
	ta_contact: yup
		.string()
		.required('Contact is required')
		.email('Please enter a valid email address'),
	ta_phone: yup
		.string()
		.required('Phone is required')
		.matches(
			/^[\d\s\-()]+$/,
			'Phone number must only contain numbers, spaces, dashes, and parentheses',
		)
		.min(10, 'Phone number must be at least 10 digits'),
	ta_streetaddress: yup.string().required('Street address is required'),
	ta_city: yup.string().required('City is required'),
	ta_statezip: yup
		.string()
		.transform((value) => (typeof value === 'string' ? value.trim() : value))
		.required('Zip/Postal code is required')
		.test('postalcode', 'Postal code must contain at least 5 digits', (value) => {
			if (!value || value === '') return false;
			// Accept only numbers and dashes
			const postalCodeRegex = /^[\d-]+$/;
			if (!postalCodeRegex.test(value)) return false;
			// Count digits only (ignore dashes)
			const digitCount = (value.match(/\d/g) || []).length;
			// Require at least 5 digits for general world validation
			return digitCount >= 5;
		}),
	products: yup
		.array()
		.of(
			yup.object().shape({
				ta_tissueid: yup
					.string()
					.required('Tissue ID is required')
					.min(1, 'Tissue ID is required'),
				ta_expirationdate: yup
					.string()
					.required('Expiration date is required')
					.min(1, 'Expiration date is required'),
				ta_datereturnedtransferred: yup
					.string()
					.required('Date returned/transferred is required')
					.min(1, 'Date returned/transferred is required'),
				ta_newshippingdaterequested: yup
					.string()
					.required('New shipping date requested is required')
					.min(1, 'New shipping date requested is required'),
			}),
		)
		.min(1, 'At least one product is required')
		.required('Products are required'),
	ta_hastheproductbeeninyourpossessionupon: yup
		.boolean()
		.required('This field is required'),
	ta_hastheproductbeenstoredaccordingtothemanu: yup
		.boolean()
		.required('This field is required'),
	ta_hastheproductbeensecurelystoredbyauthoriz: yup
		.boolean()
		.required('This field is required'),
	ta_hasanyproductbeenusedoropen: yup
		.boolean()
		.required('This field is required'),
	ta_istheproductintheoriginalunmarkedandundam: yup
		.boolean()
		.required('This field is required'),
	ta_commentsclarifications: yup.string().optional().default(''),
});

const ReturnOrderForm: React.FC<ReturnOrderFormProps> = ({
	onSuccess,
	onTitleChange,
	onIsDirtyChange,
	draftId = null,
	existingDraft,
	onExitConfirmationDraftActionChange,
}) => {
	const [showPreview, setShowPreview] = useState(false);
	const [currentStep, setCurrentStep] = useState(1);
	const [loadedDraftId, setLoadedDraftId] = useState<string | null>(draftId);
	const [isDraftLoaded, setIsDraftLoaded] = useState(false); // Track if draft was loaded
	const [hasCheckedForDraft, setHasCheckedForDraft] = useState(false); // Track if we've checked for drafts
	const user = useAuthStore((state) => state.user);
	const userId = user?.userInformation?._id || null;
	const userName = user?.userInformation ? `${user.userInformation.firstName} ${user.userInformation.lastName}` : '';

	const {
		register,
		formState: { errors, isDirty },
		setValue,
		getValues,
		watch,
		reset,
		control,
		trigger,
	} = useForm<ReturnOrderFormData>({
		resolver: yupResolver(returnOrderSchema),
		mode: 'onTouched',
		defaultValues: {
			clinic: undefined,
			ta_contact: '',
			ta_phone: '',
			ta_streetaddress: '',
			ta_city: '',
			ta_statezip: '',
			products: [
				{
					ta_tissueid: '',
					ta_expirationdate: '',
					ta_datereturnedtransferred: '',
					ta_newshippingdaterequested: '',
				},
			],
			ta_hastheproductbeeninyourpossessionupon: false,
			ta_hastheproductbeenstoredaccordingtothemanu: false,
			ta_hastheproductbeensecurelystoredbyauthoriz: false,
			ta_hasanyproductbeenusedoropen: false,
			ta_istheproductintheoriginalunmarkedandundam: false,
			ta_commentsclarifications: '',
		} as Partial<ReturnOrderFormData> as ReturnOrderFormData,
	});

	// Watch the clinic field for changes to auto-populate address fields
	const selectedClinic = watch('clinic');
	const selectedAccountId = (selectedClinic as SelectItem)?.value as
		| string
		| undefined;

	// Fetch account details when clinic is selected
	const { data: accountDetails } = useGetAccountDetails({
		accountId: selectedAccountId,
		enabled: !!selectedAccountId,
	});

	const formMethods = {
		register,
		getValues,
		setValue,
		watch,
		errors,
		reset,
		control,
	};

	// Draft save mutation - always create new drafts
	const { mutate: createDraftMutation, isLoading: isCreatingDraft } =
		useCreateDraft(() => {
			toast.success('Draft saved successfully');
			onSuccess();
		});

	const isSavingDraft = isCreatingDraft;

	const {
		mutate: createReturnOrder,
		isLoading,
		isSuccess,
		isError,
		error,
	} = useCreateBgProcess(() => {
		setTimeout(() => {
			onSuccess();
		}, 2000);
	});

	// Auto-populate form fields when account details are fetched
	// BUT: Skip auto-fill if we've loaded data from a draft
	useEffect(() => {
		if (accountDetails && !isDraftLoaded) {
			// Combine phone fields - prioritize in order: telephone1, address1_telephone1, address1_telephone2
			const phoneNumber =
				accountDetails.telephone1 ||
				accountDetails.address1_telephone1 ||
				accountDetails.address1_telephone2 ||
				'';

			// Do not validate on auto-fill; validation should happen when user
			// attempts to move to the next step, so errors are not shown
			// immediately just for selecting a clinic.
			setValue('ta_streetaddress', accountDetails.address1_line1 || '');
			setValue('ta_city', accountDetails.address1_city || '');
			setValue('ta_statezip', accountDetails.address1_postalcode || '');
			setValue('ta_phone', phoneNumber);
			setValue('ta_contact', accountDetails.emailaddress1 || '');
		}
	}, [accountDetails, setValue, isDraftLoaded]);

	// Load draft data when available
	useEffect(() => {
		if (existingDraft) {
			reset(existingDraft.formData);
			setLoadedDraftId(existingDraft._id);
			setIsDraftLoaded(true); // Mark that we've loaded from draft
			setHasCheckedForDraft(true);
		} else if (draftId || userId) {
			// If draftId was provided or we've checked for latest, mark as checked
			setHasCheckedForDraft(true);
		}
	}, [existingDraft, draftId, userId, reset]);

	useEffect(() => {
		if (onIsDirtyChange) {
			// It's negative because we want to trigger the modal to show when the form is dirty
			onIsDirtyChange(!isDirty);
		}
	}, [isDirty]);

	// Check if current step's required fields are filled
	const handleStepChange = async (step: number) => {
		// Validate required fields by step, similar to BenefitVerification
		let isValid = true;
		
		if (step === 1) {
			isValid = await trigger([
				'clinic',
				'ta_contact',
				'ta_phone',
				'ta_streetaddress',
				'ta_city',
				'ta_statezip',
			]);
		}

		if (step === 2) {
			isValid = await trigger([
				'ta_hastheproductbeeninyourpossessionupon',
				'ta_hastheproductbeenstoredaccordingtothemanu',
				'ta_hastheproductbeensecurelystoredbyauthoriz',
				'ta_hasanyproductbeenusedoropen',
				'ta_istheproductintheoriginalunmarkedandundam',
			]);
		}

		if (step === 3) {
			isValid = await trigger(['products']);
		}

		if (!isValid) {
			return false;
		}

		return true;
	};

	const handleSaveDraft = useCallback(() => {
		const formData = getValues();

		// Generate titleOrReference from clinic name or first tissue ID
		const clinicName = (formData.clinic as SelectItem)?.label || '';
		const firstTissueId = formData.products?.[0]?.ta_tissueid || '';
		const titleOrReference = clinicName || firstTissueId || 'Return Order Draft';

		const payload = {
			userId: userId || '',
			userName: userName,
			formType: FORM_TYPES.RETURN_ORDER,
			draftType: DRAFT_TYPE_LABELS.RETURN_ORDER,
			titleOrReference,
			formData,
		};

		// Always create a new draft (matching patient order form behavior)
		createDraftMutation(payload);
	}, [createDraftMutation, getValues, userId, userName]);

	useEffect(() => {
		if (!onExitConfirmationDraftActionChange) {
			return;
		}

		if (existingDraft) {
			onExitConfirmationDraftActionChange(null);
			return;
		}

		onExitConfirmationDraftActionChange({
			label: 'Save as Draft',
			onClick: handleSaveDraft,
			loading: isSavingDraft,
			disabled: isLoading || isSavingDraft,
		});

		return () => {
			onExitConfirmationDraftActionChange(null);
		};
	}, [
		existingDraft,
		handleSaveDraft,
		isLoading,
		isSavingDraft,
		onExitConfirmationDraftActionChange,
	]);

	const handleSubmitForm = () => {
		const formData = getValues();
		trackCoreClick('submit_return_order_clicked', {
			source_module: 'return_order_form',
			clinic_id: (formData.clinic as SelectItem | undefined)?.value,
			product_count: formData.products?.length || 0,
			tissue_ids: (formData.products || []).map((product) => product.ta_tissueid),
		});

		// Helper function to format date from MM/DD/YYYY to YYYY-MM-DD (without timezone conversion)
		const formatDateToYYYYMMDD = (dateStr: string): string => {
			if (!dateStr) return '';
			try {
				// Parse MM/DD/YYYY format and convert to YYYY-MM-DD without timezone conversion
				const parsed = dayjs(dateStr, 'MM/DD/YYYY', true);
				if (!parsed.isValid()) return '';
				return parsed.format('YYYY-MM-DD');
			} catch {
				return '';
			}
		};

		// Map all products with formatted dates
		const products = (formData?.products || []).map((product) => ({
			ta_tissueid: product.ta_tissueid || '',
			ta_expirationdate: formatDateToYYYYMMDD(product.ta_expirationdate || ''),
			ta_datereturnedtransferred: formatDateToYYYYMMDD(
				product.ta_datereturnedtransferred || '',
			),
			ta_newshippingdaterequested: formatDateToYYYYMMDD(
				product.ta_newshippingdaterequested || '',
			),
		}));

		const payload = {
			'ta_Customername@odata.bind': `accounts(${(formData?.clinic as SelectItem)?.value})`,
			ta_contact: formData?.ta_contact || '',
			ta_phone: formData?.ta_phone || '',
			ta_streetaddress: formData?.ta_streetaddress || '',
			ta_city: formData?.ta_city || '',
			ta_statezip: formData?.ta_statezip || '',
			products, // Send all products as array
			ta_hastheproductbeeninyourpossessionupon:
				formData?.ta_hastheproductbeeninyourpossessionupon,
			ta_hastheproductbeenstoredaccordingtothemanu:
				formData?.ta_hastheproductbeenstoredaccordingtothemanu,
			ta_hastheproductbeensecurelystoredbyauthoriz:
				formData?.ta_hastheproductbeensecurelystoredbyauthoriz,
			ta_hasanyproductbeenusedoropen: formData?.ta_hasanyproductbeenusedoropen,
			ta_istheproductintheoriginalunmarkedandundam:
				formData?.ta_istheproductintheoriginalunmarkedandundam,
			ta_commentsclarifications: formData?.ta_commentsclarifications || '',
			ta_originationsource: 953870002,
			statecode: 1,
			statuscode: 1,
		};

		createReturnOrder({
			moduleName: BgProcessModule.RETURN_REQUEST,
			userId: userId || '',
			payload: { 
				payload,
				additionalPayload: {
					clinicName: formData.clinic?.label as string,
					tissueIds: JSON.stringify(products.map((p) => p?.ta_tissueid)),
				} 
			},
		});
	};

	const stepperSteps: StepperStep[] = [
		{
			name: 'Patient Information',
			element: <CustomerInformation formMethods={formMethods} />,
		},
		{
			name: 'Compliance Checklist',
			element: <ComplianceChecklist formMethods={formMethods} />,
		},
		{
			name: 'Tissue Details',
			element: <TissueDetails formMethods={formMethods} />,
		},
	];

	const getPreviewData = () => {
		const formData = getValues();
		const productsData = formData.products || [];

		return {
			clinicName: (formData.clinic as SelectItem)?.label || '',
			ta_contact: formData.ta_contact || '',
			ta_phone: formData.ta_phone || '',
			ta_streetaddress: formData.ta_streetaddress || '',
			ta_city: formData.ta_city || '',
			ta_statezip: formData.ta_statezip || '',
			products: productsData, // Pass all products
			ta_hastheproductbeeninyourpossessionupon:
				formData.ta_hastheproductbeeninyourpossessionupon ?? false,
			ta_hastheproductbeenstoredaccordingtothemanu:
				formData.ta_hastheproductbeenstoredaccordingtothemanu ?? false,
			ta_hastheproductbeensecurelystoredbyauthoriz:
				formData.ta_hastheproductbeensecurelystoredbyauthoriz ?? false,
			ta_hasanyproductbeenusedoropen:
				formData.ta_hasanyproductbeenusedoropen ?? false,
			ta_istheproductintheoriginalunmarkedandundam:
				formData.ta_istheproductintheoriginalunmarkedandundam ?? false,
			ta_commentsclarifications: formData.ta_commentsclarifications || '',
		};
	};

	return (
		<Box pt={6}>
			<Show when={!showPreview}>
				<Stepper
					steps={stepperSteps}
					controlButtonsPosition="left"
					onStepChange={(step) => {
						setCurrentStep(step);
					}}
					validateStep={handleStepChange}
					submitButtonText="Review"
					loading={isLoading}
					onSubmit={async () => {
						// Final validation pass before showing review
						const isValid = await trigger();
						if (!isValid) return;
						setShowPreview(true);
						onTitleChange?.('Order Return Review and Confirm');
					}}
				/>
			</Show>

			<Show when={showPreview}>
				<Preview returnOrderInformation={getPreviewData()} />

				<Flex
					direction={{ base: 'column', sm: 'row' }}
					gap={{ base: 2, md: 3 }}
					mt={8}
				>
					<FaceliftButton
						width={{ base: 'full', sm: '24', md: '28', lg: '40' }}
						bg="transparent"
						color="inherit"
						border="1px solid"
						onClick={() => {
							setShowPreview(false);
							onTitleChange?.('Create Order Return');
						}}
						disabled={isLoading || isSavingDraft}
					>
						Back
					</FaceliftButton>

					<FaceliftButton
						width={{ base: 'full', sm: '24', md: '28', lg: '40' }}
						onClick={handleSubmitForm}
						loading={isLoading}
						disabled={isLoading || isSavingDraft}
					>
						Submit
					</FaceliftButton>

					{!existingDraft && (
						<FaceliftButton
							width={{ base: 'full', sm: '24', md: '28', lg: '40' }}
							bg="transparent"
							color="inherit"
							border="1px solid"
							onClick={handleSaveDraft}
							loading={isSavingDraft}
							disabled={isLoading || isSavingDraft}
						>
							Save as Draft
						</FaceliftButton>
					)}
				</Flex>
			</Show>

			<FaceliftSuccessProgressModal
				open={isLoading || isSuccess || isError}
				isLoading={isLoading}
				isSucceed={isSuccess}
				hasError={isError}
				onOpenChange={() => {}}
				title="Return order submitted successfully."
				errorMessage={
					isError
						? 'Unable to submit return order. Please check your information and try again.'
						: ''
				}
			/>
		</Box>
	);
};

export default ReturnOrderForm;
